import { CheckIcon, DownloadIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { TagInput } from '@/components/TagInput';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { emailValidator, oneOfValidator, phoneNormalizer, phoneValidator, type ImportSchema } from '@/components/workbook/import-schema';
import { WorkbookImporter, type ImportPayload, type ServerValidation } from '@/components/workbook/WorkbookImporter';
import { api, errorMessage } from '@/lib/api';
import type { ImportResult } from '@/lib/types';
import { useTenantSettings } from '@/features/org/queries';
import { useImportRows, useTags } from './queries';

/**
 * Contact import = the generic WorkbookImporter with the contact schema, a debounced server dry run
 * (existing contacts, opt-outs, unknown languages) and the tenant's phone-parsing country.
 */
export function ImportDialog({ jobId, open, onOpenChange }: { jobId: string; open: boolean; onOpenChange: (open: boolean) => void }) {
  const tags = useTags(jobId);
  const settings = useTenantSettings(open);
  const importRows = useImportRows(jobId);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [extraTags, setExtraTags] = useState<string[]>([]);
  const [defaultLanguage, setDefaultLanguage] = useState<string>('');
  const [recordConsent, setRecordConsent] = useState(false);
  const [consentText, setConsentText] = useState('Consent to receive job-site text alerts collected on onboarding paperwork.');
  const [result, setResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    if (!open) {
      setUpdateExisting(true);
      setExtraTags([]);
      setDefaultLanguage('');
      setRecordConsent(false);
      setResult(null);
    }
  }, [open]);

  const defaultCountry = settings.data?.settings.defaultCountry ?? 'US';
  const languages = settings.data?.languages ?? [];
  const enabledLanguages = settings.data?.settings.i18n.enabledLanguages ?? ['en'];
  const languageNames = useMemo(() => languages.flatMap((l) => [l.code, l.name, l.nativeName]), [languages]);

  const schema = useMemo<ImportSchema>(
    () => ({
      noun: 'contact',
      fields: [
        { key: 'phone', label: 'Mobile number', required: true, unique: true, aliases: ['phone', 'phone number', 'mobile', 'cell', 'cell phone', 'number', 'telephone', 'tel', 'sms', 'telefono', 'celular'], validate: phoneValidator(defaultCountry), normalize: phoneNormalizer(defaultCountry) },
        { key: 'firstName', label: 'First name', aliases: ['first', 'given name', 'fname', 'nombre'] },
        { key: 'lastName', label: 'Last name', aliases: ['last', 'surname', 'family name', 'lname', 'apellido'] },
        { key: 'fullName', label: 'Full name', hint: 'Split into first/last automatically', aliases: ['name', 'contact', 'contact name', 'employee', 'employee name', 'worker'] },
        { key: 'email', label: 'Email', aliases: ['email address', 'mail', 'correo'], validate: emailValidator },
        { key: 'company', label: 'Company', aliases: ['employer', 'organization', 'subcontractor', 'sub', 'vendor', 'crew', 'contractor', 'empresa'] },
        { key: 'title', label: 'Title / trade', aliases: ['title', 'role', 'position', 'trade', 'job title', 'craft', 'oficio'] },
        { key: 'language', label: 'Language', aliases: ['lang', 'preferred language', 'idioma'], validate: languageNames.length ? oneOfValidator(languageNames, 'language') : undefined },
        { key: 'tags', label: 'Tags', hint: 'Separated by ; , or |', aliases: ['tag', 'group', 'groups', 'team', 'shift'] },
        { key: 'notes', label: 'Notes', aliases: ['note', 'comments'] },
      ],
    }),
    [defaultCountry, languageNames],
  );

  const toInput = useCallback(
    (p: ImportPayload, dryRun: boolean) => ({
      fileName: p.fileName,
      sheetName: p.sheetName,
      headers: p.headers,
      rows: p.rows,
      mapping: p.mapping,
      updateExisting,
      tags: extraTags,
      dryRun,
      defaultLanguage: defaultLanguage || undefined,
      consentText: recordConsent && consentText.trim() ? consentText.trim() : undefined,
    }),
    [updateExisting, extraTags, defaultLanguage, recordConsent, consentText],
  );

  const validateOnServer = useCallback(
    async (p: ImportPayload, signal: AbortSignal): Promise<ServerValidation> => {
      const r = await api.contacts.importRows(jobId, toInput(p, true), signal);
      return {
        rows: r.rowResults.map((x) => ({ index: x.index, status: x.status, reason: x.reason, field: x.field })),
        summary: [
          { label: 'new', value: r.created, tone: 'success' },
          { label: 'updated', value: r.updated, tone: 'secondary' },
          { label: 'skipped', value: r.skipped, tone: 'muted' },
          ...(r.errorCount > 0 ? [{ label: 'errors', value: r.errorCount, tone: 'danger' as const }] : []),
        ],
        note:
          r.languages.length > 1 ? (
            <p className="text-muted-foreground text-xs">
              Languages after import: {r.languages.map((l) => `${languages.find((x) => x.code === l.language)?.name ?? l.language} ${l.count}`).join(' · ')}
            </p>
          ) : undefined,
      };
    },
    [jobId, toInput, languages],
  );

  const onImport = useCallback(
    async (p: ImportPayload) => {
      try {
        const r = await importRows.mutateAsync(toInput(p, false));
        setResult(r);
        toast.success(`Imported ${r.created} new and updated ${r.updated} contacts`);
      } catch (err) {
        toast.error(errorMessage(err));
      }
    },
    [importRows, toInput],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] flex-col overflow-hidden sm:max-w-[min(96vw,1240px)]">
        <DialogHeader>
          <DialogTitle>Import contacts</DialogTitle>
          <DialogDescription>Upload an Excel workbook or CSV, check the preview, fix anything highlighted, then import. Existing contacts are matched by phone number; opted-out numbers stay opted out.</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="grid gap-5">
            <div className="flex items-center gap-3 border p-4">
              <CheckIcon className="text-success size-5" />
              <div className="grid gap-0.5 text-sm">
                <span className="font-medium">Import complete</span>
                <span className="text-muted-foreground text-xs">
                  {result.created} new · {result.updated} updated · {result.skipped} skipped · {result.errorCount} errors
                  {result.optedOutApplied > 0 && ` · ${result.optedOutApplied} marked opted out from prior STOP replies`}
                </span>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <WorkbookImporter
            schema={schema}
            validateOnServer={validateOnServer}
            onImport={onImport}
            validationKey={`${updateExisting}|${extraTags.join(',')}|${defaultLanguage}`}
            importLabel={({ rows, errors }) => `Import ${Math.max(0, rows - errors).toLocaleString()} contacts`}
            pickerExtras={
              <>
                <span className="text-muted-foreground text-xs">Need a starting point?</span>
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => void api.contacts.templateXlsx(jobId).catch((e: unknown) => toast.error(errorMessage(e)))}>
                    <DownloadIcon /> Excel template
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => void api.contacts.template(jobId).catch((e: unknown) => toast.error(errorMessage(e)))}>
                    <DownloadIcon /> CSV template
                  </Button>
                </div>
              </>
            }
            options={
              <div className="grid gap-4 border bg-card p-3">
                <h4 className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">Options</h4>
                <label className="flex items-start gap-3">
                  <Checkbox checked={updateExisting} onCheckedChange={(v) => setUpdateExisting(Boolean(v))} className="mt-0.5" />
                  <span className="grid gap-0.5">
                    <span className="text-sm font-medium">Update existing contacts</span>
                    <span className="text-muted-foreground text-xs">When a phone number already exists on this job, update its details instead of skipping.</span>
                  </span>
                </label>
                <div className="grid gap-2">
                  <Label>Add tags to every imported contact</Label>
                  <TagInput value={extraTags} onChange={setExtraTags} suggestions={(tags.data?.items ?? []).map((t) => t.tag)} placeholder="e.g. week-1" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="imp-lang">Language when a row has none</Label>
                  <select id="imp-lang" value={defaultLanguage} onChange={(e) => setDefaultLanguage(e.target.value)} className="border-input bg-background h-9 w-full border px-2 text-sm">
                    <option value="">Organization default ({languages.find((l) => l.code === settings.data?.settings.i18n.defaultLanguage)?.name ?? 'English'})</option>
                    {enabledLanguages.map((code) => (
                      <option key={code} value={code}>
                        {languages.find((l) => l.code === code)?.name ?? code}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="flex items-start gap-3">
                  <Checkbox checked={recordConsent} onCheckedChange={(v) => setRecordConsent(Boolean(v))} className="mt-0.5" />
                  <span className="grid gap-0.5">
                    <span className="text-sm font-medium">Record consent for new contacts</span>
                    <span className="text-muted-foreground text-xs">Stores a TCPA consent entry noting how these people agreed to receive texts.</span>
                  </span>
                </label>
                {recordConsent && <Textarea rows={3} value={consentText} onChange={(e) => setConsentText(e.target.value)} className="text-xs" maxLength={600} />}
              </div>
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
